
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Trash2, Search, Eye, Plus, Pencil } from 'lucide-react';
import { adminService } from '@/services/admin';
import { useToast } from '@/hooks/use-toast';

const Articles = () => {
  const [articles, setArticles] = useState<any[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<any>(null);
  const [newArticle, setNewArticle] = useState({
    title: '',
    content: '',
    summary: '',
    category: 'general',
    cover_image_url: '',
    status: 'draft',
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    const filtered = articles.filter(article =>
      article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredArticles(filtered);
  }, [articles, searchTerm]);

  const fetchArticles = async () => {
    try {
      const data = await adminService.getArticles();
      setArticles(data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast({
        variant: 'destructive',
        title: '載入失敗',
        description: '無法載入文章列表',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateArticle = async () => {
    try {
      await adminService.createArticle(newArticle);
      await fetchArticles();
      setIsCreateDialogOpen(false);
      setNewArticle({
        title: '',
        content: '',
        summary: '',
        category: 'general',
        cover_image_url: '',
        status: 'draft',
      });
      toast({
        title: '建立成功',
        description: '文章已建立',
      });
    } catch (error) {
      console.error('Error creating article:', error);
      toast({
        variant: 'destructive',
        title: '建立失敗',
        description: '無法建立文章',
      });
    }
  };

  const handleUpdateArticle = async () => {
    if (!editingArticle) return;
    
    try {
      await adminService.updateArticle(editingArticle.id, editingArticle);
      await fetchArticles();
      setIsEditDialogOpen(false);
      setEditingArticle(null);
      toast({
        title: '更新成功',
        description: '文章已更新',
      });
    } catch (error) {
      console.error('Error updating article:', error);
      toast({
        variant: 'destructive',
        title: '更新失敗',
        description: '無法更新文章',
      });
    }
  };

  const handleDeleteArticle = async (articleId: string) => {
    if (window.confirm('確定要刪除此文章嗎？此操作無法復原。')) {
      try {
        await adminService.deleteArticle(articleId);
        await fetchArticles();
        toast({
          title: '刪除成功',
          description: '文章已刪除',
        });
      } catch (error) {
        console.error('Error deleting article:', error);
        toast({
          variant: 'destructive',
          title: '刪除失敗',
          description: '無法刪除文章',
        });
      }
    }
  };

  const handleToggleStatus = async (articleId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      await adminService.updateArticle(articleId, { status: newStatus });
      await fetchArticles();
      toast({
        title: '狀態更新',
        description: `文章已${newStatus === 'published' ? '發布' : '設為草稿'}`,
      });
    } catch (error) {
      console.error('Error updating article status:', error);
      toast({
        variant: 'destructive',
        title: '更新失敗',
        description: '無法更新文章狀態',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">文章管理</h1>
          <p className="text-gray-600 dark:text-gray-400">管理平台所有部落格文章</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              新增文章
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>建立新文章</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                placeholder="文章標題"
                value={newArticle.title}
                onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
              />
              <Textarea
                placeholder="文章摘要"
                value={newArticle.summary}
                onChange={(e) => setNewArticle({ ...newArticle, summary: e.target.value })}
              />
              <Select
                value={newArticle.category}
                onValueChange={(value) => setNewArticle({ ...newArticle, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="選擇分類" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">一般</SelectItem>
                  <SelectItem value="tips">租房技巧</SelectItem>
                  <SelectItem value="legal">法律相關</SelectItem>
                  <SelectItem value="market">市場分析</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="封面圖片 URL（選填）"
                value={newArticle.cover_image_url}
                onChange={(e) => setNewArticle({ ...newArticle, cover_image_url: e.target.value })}
              />
              <Textarea
                placeholder="文章內容"
                value={newArticle.content}
                onChange={(e) => setNewArticle({ ...newArticle, content: e.target.value })}
                rows={8}
              />
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  取消
                </Button>
                <Button onClick={handleCreateArticle}>
                  建立文章
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>文章列表</CardTitle>
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="搜尋文章..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>標題</TableHead>
                <TableHead>分類</TableHead>
                <TableHead>作者</TableHead>
                <TableHead>狀態</TableHead>
                <TableHead>發布時間</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredArticles.map((article) => (
                <TableRow key={article.id}>
                  <TableCell>
                    <div className="font-medium max-w-xs truncate">
                      {article.title}
                    </div>
                    {article.summary && (
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {article.summary}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {article.category === 'general' && '一般'}
                      {article.category === 'tips' && '租房技巧'}
                      {article.category === 'legal' && '法律相關'}
                      {article.category === 'market' && '市場分析'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {article.profiles?.nickname || '匿名作者'}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleStatus(article.id, article.status)}
                    >
                      <Badge variant={article.status === 'published' ? 'default' : 'secondary'}>
                        {article.status === 'published' ? '已發布' : '草稿'}
                      </Badge>
                    </Button>
                  </TableCell>
                  <TableCell>
                    {new Date(article.created_at).toLocaleDateString('zh-TW')}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/article/${article.id}`, '_blank')}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Dialog open={isEditDialogOpen && editingArticle?.id === article.id} onOpenChange={setIsEditDialogOpen}>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingArticle(article);
                              setIsEditDialogOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>編輯文章</DialogTitle>
                          </DialogHeader>
                          {editingArticle && (
                            <div className="space-y-4">
                              <Input
                                placeholder="文章標題"
                                value={editingArticle.title}
                                onChange={(e) => setEditingArticle({ ...editingArticle, title: e.target.value })}
                              />
                              <Textarea
                                placeholder="文章摘要"
                                value={editingArticle.summary || ''}
                                onChange={(e) => setEditingArticle({ ...editingArticle, summary: e.target.value })}
                              />
                              <Select
                                value={editingArticle.category}
                                onValueChange={(value) => setEditingArticle({ ...editingArticle, category: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="選擇分類" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="general">一般</SelectItem>
                                  <SelectItem value="tips">租房技巧</SelectItem>
                                  <SelectItem value="legal">法律相關</SelectItem>
                                  <SelectItem value="market">市場分析</SelectItem>
                                </SelectContent>
                              </Select>
                              <Textarea
                                placeholder="文章內容"
                                value={editingArticle.content}
                                onChange={(e) => setEditingArticle({ ...editingArticle, content: e.target.value })}
                                rows={8}
                              />
                              <div className="flex justify-end space-x-2">
                                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                                  取消
                                </Button>
                                <Button onClick={handleUpdateArticle}>
                                  更新文章
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteArticle(article.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Articles;
